import { createApp } from 'vue'
import { defineNuxtPlugin } from 'nuxt/app'
import NotificationCaster from './NotificationCaster.vue'
import { generateHash } from '@/utils/general'
import type { PushArguments } from '@/types/notification/PushArguments'
import type { NotificationEvent } from '@/types/notification/NotificationEvent'

// Create Notification Caster and return the container element
export const createNotificationCaster = (): HTMLDivElement => {
  const instance = createApp(NotificationCaster)
  const container = document.createElement('div')
  document.body.appendChild(container) // Ensure there's a container to mount it to
  instance.mount(container)
  return container // Return the container element
}
const push = (args: PushArguments): Promise<boolean> => {
  const { type, message, title, link, width, timeout } = args
  const hash = generateHash()

  const pushEvent = new CustomEvent('arNotificationPush', {
    detail: {
      payload: {
        type,
        message,
        title,
        link,
        width,
        timeout,
      },
      hash,
    },
  })

  window.dispatchEvent(pushEvent)

  return new Promise((resolve) => {
    const receiveHandler = (e: CustomEvent<{ answer: boolean, hash: string }>) => {
      if (e.detail.hash !== hash) return

      resolve(e.detail.answer)
      window.removeEventListener('arNotificationReceive', receiveHandler as EventListener)
    }

    window.addEventListener('arNotificationReceive', receiveHandler as EventListener)
  })
}

// Type augmentation for Nuxt 3
declare module '#app' {
  interface NuxtApp {
    $arNotification: {
      push: (args: PushArguments) => Promise<boolean>
    }
  }
}

declare module 'vue' {
  interface ComponentCustomProperties {
    $arNotification: {
      push: (args: PushArguments) => Promise<boolean>
    }
  }
}

// Nuxt plugin definition to provide global access to `arNotification`
export default defineNuxtPlugin((nuxtApp) => {
  // Create and append the NotificationCaster component to the DOM
  createNotificationCaster()

  // Provide `arNotification` to the app's context, making it globally accessible
  nuxtApp.provide('arNotification', { push })
})
